/**
 * CRMObjectClient - Generic client for CRM object operations.
 *
 * Provides CRUD operations for any CRM object type with type safety.
 */

import { apiCall, apiGet, apiPost } from '@/lib/http/client';
import { retryOn503 } from '@/lib/http/retry';
import { logger } from '@/lib/logger';
import type {
  BulkActionJob,
  BulkActionJobStatus,
  BulkChangeStageSuccessResponse,
  BulkDeleteSuccessResponse,
  BulkEnrichSuccessResponse,
  BulkUpdateSuccessResponse,
  CrmObject,
  CrmObjectFilters,
  CrmObjectsListResponse,
  EntityTypeName,
  EventBatch,
  FilterGroup,
  SubmitEventsResponse,
} from '@/types/crm';
import { isAsyncSingleObjectDeleteResponse } from '@/types/crm';

import { PropertyTypeConflictError } from './errors';
import { processOptimisticUpdates } from './EventsClient';

/** Reference to all entities of a given kind (e.g. 'event'). */
export interface EntityKindRef {
  entity_kind: string;
}

/**
 * Association type name. Currently typed as `string` because the codebase has
 * no central enum of association names (custom org-defined associations exist
 * at runtime). The backend rejects `*_timeline` names — never pass one here.
 */
export type AssociationTypeName = string;

/**
 * Discriminated selection input for bulk-action endpoints. Callers must pass
 * one of:
 *   - `entity_ids`: explicit row ids selected in the UI (sync only).
 *   - `filters`: "select all matching" with at least one positive selector
 *     (filterGroups or search). `excludedEntityIds` alone is NOT a selector
 *     — it's a refinement of a wider set.
 *   - `all`: explicit opt-in to "all records of this type" with no positive
 *     filter (e.g. user selected the master checkbox on an unfiltered list).
 *     `excludedEntityIds` is still honored.
 *
 * The discriminator + runtime guard in `bulkSelectionToPayload` prevent
 * accidentally sending a selection with no effective scope (which the server
 * interprets as "all entities of this type"). Callers that genuinely want
 * unscoped behavior must opt in via `kind: 'all'`.
 */
export type BulkActionSelection =
  | {
      kind: 'entity_ids';
      entityIds: string[];
    }
  | {
      kind: 'filters';
      filterGroups?: FilterGroup[];
      excludedEntityIds?: string[];
      search?: string;
      searchProperties?: string[];
    }
  | {
      kind: 'all';
      excludedEntityIds?: string[];
    };

function bulkSelectionToPayload(
  selection: BulkActionSelection
): Record<string, unknown> {
  if (selection.kind === 'entity_ids') {
    if (!selection.entityIds || selection.entityIds.length === 0) {
      throw new Error(
        "BulkActionSelection: kind='entity_ids' requires a non-empty entityIds array"
      );
    }
    return { entity_ids: selection.entityIds };
  }
  if (selection.kind === 'all') {
    return {
      excluded_entity_ids: selection.excludedEntityIds,
    };
  }
  // kind === 'filters' — require at least one positive selector. An empty
  // selection here would silently target every record of the entity type.
  const hasFilters =
    !!selection.filterGroups && selection.filterGroups.length > 0;
  const hasSearch = selection.search != null && selection.search !== '';
  if (!hasFilters && !hasSearch) {
    throw new Error(
      "BulkActionSelection: kind='filters' requires filterGroups or search; " +
        "use kind='all' to explicitly target every record of this type"
    );
  }
  return {
    filter_groups: selection.filterGroups,
    excluded_entity_ids: selection.excludedEntityIds,
    ...(selection.search
      ? {
          search: selection.search,
          search_properties: selection.searchProperties,
        }
      : {}),
  };
}

/**
 * Build the query-string suffix for the retrieve endpoint.
 *
 * Both flags default to opt-in. The backend treats
 * `include_associated_objects=true` as implying `include_associations=true`,
 * but we still emit both for clarity in HTTP traces and to keep the React
 * Query cache key meaningful when only one flag changes.
 */
function buildRetrieveQueryString(options?: {
  includeAssociatedObjects?: boolean;
  includeAssociations?: boolean;
}): string {
  const params: string[] = [];
  if (options?.includeAssociatedObjects) {
    params.push('include_associated_objects=true');
  }
  if (options?.includeAssociations) {
    params.push('include_associations=true');
  }
  return params.length > 0 ? `?${params.join('&')}` : '';
}

/**
 * Generic CRM object client.
 */
export class CRMObjectClient {
  private orgId: number;
  private entityRef: string | EntityKindRef;

  constructor(orgId: number, entityRef: string | EntityKindRef) {
    this.orgId = orgId;
    this.entityRef = entityRef;
  }

  /**
   * List objects of this type using the new POST query endpoint.
   *
   * @param filters - Standard CRM filters (must include properties array)
   * @param options - Additional query options
   * @param options.include_associated_objects - Fetch all associated objects (legacy)
   * @param options.include_associations - Fetch specific associations with selected properties
   * @param options.signal - Optional AbortSignal to cancel the request
   *
   * NOTE: associations are OPT-IN on the query endpoint (aligned with `get`/
   * `getByEntityIds` below). A list call without one of the two include options
   * returns results with NO `associations` key — the backend also skips the
   * association-target accessibility fan-out, which on high-degree entities
   * dominated response time. Callers that read `.associations` from list
   * results MUST pass `include_associations` with the specific types they need
   * (never request all association types from the frontend).
   */
  async list(
    filters?: Omit<CrmObjectFilters, 'entity_type_name' | 'entity_kind'>,
    options?: {
      include_associated_objects?: boolean;
      include_associations?: Record<string, { properties: string[] }>;
      signal?: AbortSignal;
    }
  ): Promise<CrmObjectsListResponse> {
    // Build request body for new POST /query endpoint.
    // Use entity_kind XOR entity_type_name — the backend treats them as mutually exclusive.
    const requestBody: Record<string, unknown> =
      typeof this.entityRef === 'string'
        ? { entity_type_name: this.entityRef }
        : { entity_kind: this.entityRef.entity_kind };

    if (filters) {
      // Forward an explicit properties array (including []). [] tells the backend
      // to return no property values (count-only callers); undefined omits the
      // field entirely, preserving the all-properties fallback.
      if (Array.isArray(filters.properties)) {
        requestBody.properties = filters.properties;
      }

      // Add filter groups (OR logic between groups, AND within groups)
      if (
        filters.filter_groups &&
        Array.isArray(filters.filter_groups) &&
        filters.filter_groups.length > 0
      ) {
        requestBody.filter_groups = filters.filter_groups;
      }

      // Add simple filters
      if (filters.associated_with_entity_id) {
        requestBody.associated_with_entity_id =
          filters.associated_with_entity_id;
      }

      if (filters.include_deleted !== undefined) {
        requestBody.include_deleted = filters.include_deleted;
      }

      if (filters.include_total_count !== undefined) {
        requestBody.include_total_count = filters.include_total_count;
      }

      if (filters.include_associated_objects !== undefined) {
        requestBody.include_associated_objects =
          filters.include_associated_objects;
      }

      if (filters.search) {
        requestBody.search = filters.search;
      }

      if (
        filters.search_properties &&
        Array.isArray(filters.search_properties) &&
        filters.search_properties.length > 0
      ) {
        requestBody.search_properties = filters.search_properties;
      }

      // Handle sorting - support both new (sort/order) and legacy (order_by) formats
      if (filters.order_by && typeof filters.order_by === 'string') {
        // Legacy format: "-created_at" or "created_at"
        const isDesc = filters.order_by.startsWith('-');
        requestBody.sort = isDesc
          ? filters.order_by.substring(1)
          : filters.order_by;
        requestBody.order = isDesc ? 'desc' : 'asc';
      } else {
        // New format: separate sort and order fields
        if (filters.sort) {
          requestBody.sort = filters.sort;
        }
        if (filters.order) {
          requestBody.order = filters.order;
        }
      }

      // Pagination: Convert page/page_size to limit/offset, or use direct limit/offset
      const page = filters.page as number | undefined;
      const pageSize = filters.page_size as number | undefined;
      if (page !== undefined && pageSize !== undefined) {
        // Validate and convert page/page_size to limit/offset (page is 1-indexed)
        if (pageSize > 0) {
          const safePage = Math.max(1, page);
          requestBody.limit = pageSize;
          requestBody.offset = (safePage - 1) * pageSize;
        }
      } else {
        // Direct limit/offset passthrough (existing behavior)
        if (filters.limit !== undefined) {
          requestBody.limit = filters.limit;
        }
        if (filters.offset !== undefined) {
          requestBody.offset = filters.offset;
        }
      }
    }

    // Add options - include_associations takes precedence over include_associated_objects
    // (they are mutually exclusive on the backend; sending both returns 400)
    if (options?.include_associations) {
      requestBody.include_associations = options.include_associations;
    } else if (options?.include_associated_objects === true) {
      requestBody.include_associated_objects = true;
    }

    // Use POST /query endpoint. dedupeInFlight: the query endpoint is
    // read-only, and identical list queries are frequently issued
    // concurrently under different React Query keys (dashboard widgets,
    // record-page sections, WS-invalidation refetch bursts) — share one
    // network call. Requests carrying an AbortSignal are never deduped
    // (see apiCall).
    const url = `/api/orgs/${this.orgId}/crm-events/crm-objects/query/`;
    return retryOn503(() =>
      apiCall<CrmObjectsListResponse>(
        url,
        'POST',
        requestBody,
        undefined,
        options?.signal,
        true
      )
    );
  }

  /**
   * Get a single object by ID (can be either object_id or entity_id).
   *
   * @param id - Object PK or entity_id
   * @param options.includeAssociatedObjects - Legacy opt-in that enriches the
   *   response with an `associated_objects` lookup map keyed by entity_id.
   *   Defaults to false. The full-page record view no longer requests this
   *   (it dropped the 416KB map in favour of hydrating association targets via
   *   the by-entity-ids batcher). Only pass `true` from callers that
   *   genuinely need the pre-fetched map and cannot use the batcher instead.
   * @param options.includeAssociations - When true, the response includes the
   *   access-controlled `associations` array on the object. Defaults to false
   *   because the underlying permission check
   *   (compute_accessible_association_targets) fans out into thousands of DB
   *   queries for high-degree entities (15k+ associations on founder /
   *   active-user records). Pass `true` from views that render association
   *   badges or read `.associations` (e.g. record-detail page, pipeline
   *   conflict-retry path). Implied automatically when
   *   `includeAssociatedObjects` is true (server-side coupling).
   */
  async get(
    id: string | number,
    options?: {
      includeAssociatedObjects?: boolean;
      includeAssociations?: boolean;
    }
  ): Promise<CrmObject> {
    const qs = buildRetrieveQueryString(options);
    return apiCall<CrmObject>(
      `/api/orgs/${this.orgId}/crm-events/crm-objects/${id}/${qs}`
    );
  }

  /**
   * Get a CRM object by its entity UUID.
   * Use this when you have a entity_id (UUID string).
   * For numeric pk lookups, use get() instead.
   *
   * @param entityId - Entity UUID
   * @param options.includeAssociatedObjects - See {@link get} — legacy opt-in
   *   for the `associated_objects` map. The full-page record view no longer
   *   uses this; record-detail hydrates association targets via the
   *   by-entity-ids batcher instead.
   * @param options.includeAssociations - See {@link get} — opt in when the
   *   caller reads `.associations` from the response.
   */
  async getByEntityId(
    entityId: string,
    options?: {
      includeAssociatedObjects?: boolean;
      includeAssociations?: boolean;
    }
  ): Promise<CrmObject> {
    const qs = buildRetrieveQueryString(options);
    return apiCall<CrmObject>(
      `/api/orgs/${this.orgId}/crm-events/crm-objects/${entityId}/${qs}`
    );
  }

  /**
   * Batch fetch CRM objects by entity UUIDs.
   *
   * Uses the by-entity-ids endpoint which correctly handles merged entities
   * by keying results by the *requested* entity_id (not the canonical one).
   * Max 100 entity IDs per request.
   *
   * `includeAssociations` is opt-in (default false): the server returns lean
   * objects with no `associations` key unless it is true. The flag is
   * transmitted on EVERY request — an earlier encoding sent it only when
   * false, which meant `true` silently rode on the server's then-True default
   * and would have broken merge (silently — MergeWizard swallows fetch
   * errors) the moment the server default changed. Association hydration is
   * per-target permission-gated and expensive for high-degree entities, so
   * pass true only when the caller actually reads `.associations` off the
   * result.
   *
   * Note: This is entity-type-agnostic — the endpoint accepts any entity IDs
   * regardless of entity type, so `this.entityType` is NOT used.
   */
  async getByEntityIds(
    entityIds: string[],
    options?: { includeAssociations?: boolean; properties?: string[] }
  ): Promise<Record<string, CrmObject>> {
    const payload: Record<string, unknown> = {
      entity_ids: entityIds,
      include_associations: options?.includeAssociations === true,
    };
    // When provided, the server scopes both the DB read and the serialized
    // output to just these property names (display-name-only callers use this
    // to avoid hydrating the full object).
    if (options?.properties) {
      payload.properties = options.properties;
    }
    const response = await apiPost<{ objects: Record<string, CrmObject> }>(
      `/api/orgs/${this.orgId}/crm-events/crm-objects/by-entity-ids/`,
      payload
    );
    return response.objects;
  }

  /**
   * Bulk delete CRM objects.
   *
   * See `bulkChangeStage` for the selection modes. An explicit `entity_ids`
   * selection is resolved by the server in a single request, so the caller
   * does not loop; filter-based selections process one chunk per request (up
   * to CRM_EVENT_BATCH_SIZE) and the caller loops while has_more is true.
   *
   * Deleting a set of rows MUST go through this one call rather than one
   * request per row: the events endpoint is throttled per org (100/min), so
   * fanning out a request per record trips the throttle on a large selection
   * and the rejected rows surface to the user as failed deletes.
   *
   * Throws on non-2xx responses (e.g. selection_too_large = HTTP 400).
   * Callers should catch and use translateBulkDeleteError(error_type) for copy.
   */
  async bulkDelete(
    entityTypeName: string,
    selection: BulkActionSelection
  ): Promise<BulkDeleteSuccessResponse> {
    return apiPost<BulkDeleteSuccessResponse>(
      `/api/orgs/${this.orgId}/crm-events/bulk-action/delete/`,
      {
        entity_type_name: entityTypeName,
        ...bulkSelectionToPayload(selection),
      }
    );
  }

  /**
   * Bulk change the stage for CRM objects.
   *
   * Selection modes (mutually exclusive — pass one):
   *   - `entityIds`: explicit list of selected rows (sync only).
   *   - `filterGroups` + `excludedEntityIds` + `search`: filter-based
   *     "select all matching" selection.
   *
   * `entityTypeName` and `associationTypeName` are constrained to known CRM
   * identifiers — `EntityTypeName` excludes the `*_timeline` reserved names
   * that the backend rejects.
   *
   * Throws on non-2xx. Use translateBulkChangeStageError(error_type) for copy.
   */
  async bulkChangeStage(
    entityTypeName: EntityTypeName,
    associationTypeName: AssociationTypeName,
    targetEntityId: string,
    selection: BulkActionSelection,
    allowPipelineChange?: boolean
  ): Promise<BulkChangeStageSuccessResponse> {
    return apiPost<BulkChangeStageSuccessResponse>(
      `/api/orgs/${this.orgId}/crm-events/bulk-action/change-stage/`,
      {
        entity_type_name: entityTypeName,
        association_type_name: associationTypeName,
        target_entity_id: targetEntityId,
        // Default false: the server refuses to move a deal into a stage from a
        // different pipeline unless the caller (the "Move to pipeline" action)
        // explicitly opts in.
        allow_pipeline_change: allowPipelineChange ?? false,
        ...bulkSelectionToPayload(selection),
      }
    );
  }

  /**
   * Bulk enrich CRM objects.
   *
   * See `bulkChangeStage` for the selection modes.
   *
   * Throws on non-2xx. Use translateBulkEnrichError(error_type) for copy.
   */
  async bulkEnrich(
    entityTypeName: EntityTypeName,
    selection: BulkActionSelection
  ): Promise<BulkEnrichSuccessResponse> {
    return apiPost<BulkEnrichSuccessResponse>(
      `/api/orgs/${this.orgId}/crm-events/bulk-action/enrich/`,
      {
        entity_type_name: entityTypeName,
        ...bulkSelectionToPayload(selection),
      }
    );
  }

  /**
   * Bulk update a property value for CRM objects.
   *
   * See `bulkChangeStage` for the selection modes.
   *
   * `value` is the raw JSON value for the property's field type.
   * Null/empty values are rejected — bulk clear is not supported.
   *
   * Throws on non-2xx. Use translateBulkUpdateError(error_type) for copy.
   */
  async bulkUpdateProperty(
    entityTypeName: EntityTypeName,
    propertyTypeName: string,
    value: unknown,
    selection: BulkActionSelection
  ): Promise<BulkUpdateSuccessResponse> {
    return apiPost<BulkUpdateSuccessResponse>(
      `/api/orgs/${this.orgId}/crm-events/bulk-action/update-property/`,
      {
        entity_type_name: entityTypeName,
        property_type_name: propertyTypeName,
        value,
        ...bulkSelectionToPayload(selection),
      }
    );
  }

  /**
   * Bulk update an association for CRM objects.
   *
   * See `bulkChangeStage` for the selection modes.
   *
   * The server enforces pipeline-change rules automatically.
   *
   * Throws on non-2xx. Use translateBulkUpdateError(error_type) for copy.
   */
  async bulkUpdateAssociation(
    entityTypeName: EntityTypeName,
    associationTypeName: AssociationTypeName,
    targetEntityId: string,
    selection: BulkActionSelection
  ): Promise<BulkUpdateSuccessResponse> {
    return apiPost<BulkUpdateSuccessResponse>(
      `/api/orgs/${this.orgId}/crm-events/bulk-action/update-association/`,
      {
        entity_type_name: entityTypeName,
        association_type_name: associationTypeName,
        target_entity_id: targetEntityId,
        ...bulkSelectionToPayload(selection),
      }
    );
  }

  /**
   * Fetch the current state of a BulkActionJob.
   *
   * Throws on non-2xx; callers can catch and inspect
   * `(err as { statusCode?: number }).statusCode === 404` to detect a
   * deleted/expired job.
   */
  async getBulkActionJob(jobId: number): Promise<BulkActionJob> {
    return apiGet<BulkActionJob>(
      `/api/orgs/${this.orgId}/crm-events/bulk-action/jobs/${jobId}/`
    );
  }

  /**
   * Cancel a BulkActionJob. Idempotent; returns the resulting status.
   */
  async cancelBulkActionJob(jobId: number): Promise<{
    success: true;
    status: BulkActionJobStatus;
    transitioned: boolean;
  }> {
    return apiPost<{
      success: true;
      status: BulkActionJobStatus;
      transitioned: boolean;
    }>(
      `/api/orgs/${this.orgId}/crm-events/bulk-action/jobs/${jobId}/cancel/`,
      {}
    );
  }

  /**
   * Submit events to the event-sourced CRM.
   *
   * @deprecated Use EventsClient.submitEvents() instead for new code.
   * This method is kept for backwards compatibility.
   */
  async submitEvents(batch: EventBatch): Promise<SubmitEventsResponse> {
    try {
      const response = await apiCall<SubmitEventsResponse>(
        `/api/orgs/${this.orgId}/crm-events/events/`,
        'POST',
        batch
      );
      if (isAsyncSingleObjectDeleteResponse(response)) {
        // Keep the optimistic removal applied while the job runs (Key Decision #2).
        processOptimisticUpdates(batch.events, this.orgId);
        return response;
      }
      // Prefer response results for optimistic updates — the backend may rewrite
      // events. Fall back to submitted events if results aren't enriched.
      processOptimisticUpdates(response.results ?? batch.events, this.orgId);
      return response;
    } catch (error) {
      // Map 409 Conflict to PropertyTypeConflictError for seq collision handling
      const apiError = error as {
        statusCode?: number;
        response?: {
          error?: string;
          detail?: {
            expected_seq?: number;
            last_seq?: number;
            entity_id?: string;
            last_changed_by?: { name: string; source_type: string };
          };
          correlation_id?: string;
        };
      };
      if (
        apiError &&
        typeof apiError === 'object' &&
        'statusCode' in apiError &&
        typeof apiError.statusCode === 'number' &&
        apiError.statusCode === 409
      ) {
        // Log original error before throwing PropertyTypeConflictError
        logger.error('Sequence conflict from API', error as Error, {
          component: 'crm_events',
          statusCode: apiError.statusCode,
          correlationId: apiError.response?.correlation_id,
        });
        throw new PropertyTypeConflictError(
          apiError.response?.error || 'Sequence conflict',
          apiError.response?.detail,
          apiError.response?.correlation_id
        );
      }
      throw error;
    }
  }
}

/**
 * Factory function for creating CRM object clients.
 *
 * Accepts either a string entity type name or an EntityKindRef object for kind-based queries.
 * When an EntityKindRef is passed, the client will use `entity_kind` in the request body
 * instead of `entity_type_name`.
 */
export function createCRMObjectClient(
  orgId: number,
  entityRef: string | EntityKindRef
): CRMObjectClient {
  return new CRMObjectClient(orgId, entityRef);
}
